import React, { useState } from 'react';
import { DatePicker, DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import moment from 'moment';
import { useStore } from '../../../../stores/root';
import { IOperationItem } from '../types';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import GeneralService from '../../../../services/general';
import ErrorService from '../../../../services/general/error';
import ShipScheduleDelaysService from '../../../../services/data/ships/shipScheduleDelays';
import OrderTrackingService from '../../../../services/orders/trackings/orderTracking';
import { faCalendarDays, faCheck, faClockRotateLeft, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IRescheduleModalProps {
    item: IOperationItem;
    allTrackingIds?: string[];
    isOpen: boolean;
    onDismiss(refresh?: boolean): void;
}

const RESCHEDULE_REASONS: IDropdownOption[] = [
    { key: 'Vessel Delay / Not Docked', text: 'Vessel Delay / Not Docked' },
    { key: 'Bad Weather / High Waves', text: 'Bad Weather / High Waves' },
    { key: 'Shipping Line / Vendor Reschedule', text: 'Shipping Line / Vendor Reschedule' },
    { key: 'Port Congestion', text: 'Port Congestion' },
    { key: 'Field / Depot / Warehouse Constraints', text: 'Field / Depot / Warehouse Constraints' },
    { key: 'Truck Fleet / Driver Availability', text: 'Truck Fleet / Driver Availability' },
    { key: 'Customer Request', text: 'Customer Request' },
    { key: 'Other', text: 'Other (Specify in Notes)' },
];

const parseSafeDate = (d?: string): moment.Moment => {
    if (!d) return moment();
    if (moment(d, 'YYYY-MM-DDTHH:mm:ss.SSSZ', true).isValid() || moment(d).isValid()) {
        return moment(d);
    }
    if (moment(d, 'DD/MM/YYYY', true).isValid()) {
        return moment(d, 'DD/MM/YYYY');
    }
    if (moment(d, 'YYYY-MM-DD', true).isValid()) {
        return moment(d, 'YYYY-MM-DD');
    }
    return moment(d);
};

const RescheduleModal: React.FC<IRescheduleModalProps> = ({ item, allTrackingIds, isOpen, onDismiss }) => {
    const { banner } = useStore();
    const [submitting, setSubmitting] = useState<boolean>(false);
    
    // Tanggal Mulai: Default diambil otomatis dari jadwal yang sudah ada di order
    const [newDepartureDate, setNewDepartureDate] = useState<Date>(() => {
        const parsed = parseSafeDate(item.scheduledDate);
        return parsed.isValid() ? parsed.toDate() : new Date();
    });

    // Tanggal Selesai Baru: Target tanggal selesai yang mundur (dipilih user)
    const [newArrivalDate, setNewArrivalDate] = useState<Date | undefined>(() => {
        if (item.estimatedArrival) {
            const parsed = parseSafeDate(item.estimatedArrival);
            if (parsed.isValid()) return parsed.toDate();
        }
        const parsedDep = parseSafeDate(item.scheduledDate);
        return parsedDep.isValid() ? parsedDep.add(1, 'days').toDate() : moment().add(1, 'days').toDate();
    });

    const [reason, setReason] = useState<string>(RESCHEDULE_REASONS[0].key as string);
    const [notes, setNotes] = useState<string>('');

    const isShip = item.itemType === 'ship';

    const getStartDateLabel = () => {
        if (isShip) return 'Departure Date (ETD)';
        if (item.activityType === 'dooring') return 'Tgl Keberangkatan (Start)';
        if (item.activityType === 'loading') return 'Tgl Mulai Muat (Start)';
        if (item.activityType === 'unloading') return 'Tgl Mulai Bongkar (Start)';
        return 'Tanggal Mulai (Start Date)';
    };

    const getEndDateLabel = () => {
        if (isShip) return 'New Estimated Arrival (ETA) *';
        if (item.activityType === 'dooring') return 'Target Tiba di Tujuan (ETA) *';
        if (item.activityType === 'loading') return 'Target Selesai Muat (New End Date) *';
        if (item.activityType === 'unloading') return 'Target Selesai Bongkar (New End Date) *';
        return 'Target Selesai Baru (New End Date) *';
    };

    const isSubmitDisabled = !newDepartureDate || !newArrivalDate || submitting;

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const depIso = moment(newDepartureDate).toISOString();
            const arrIso = moment(newArrivalDate || newDepartureDate).toISOString();

            if (isShip) {
                // Ship schedule delay
                const fd = new FormData();
                fd.append('scheduleId', item.rawId);
                fd.append('newEstimatedDeparture', depIso);
                fd.append('newEstimatedArrival', arrIso);
                if (reason) fd.append('reason', reason);
                if (notes) fd.append('notes', notes);

                await ShipScheduleDelaysService.new(fd);
            } else if (item.orderId) {
                const fd = new FormData();
                const trackingType = item.rawObject?.type || (item.activityType === 'unloading' ? 'unload' : (item.activityType === 'loading' ? 'load' : 'truck'));
                fd.append('type', trackingType);

                if (item.activityType === 'loading') {
                    fd.append('estimatedStartDate', depIso);
                    fd.append('estimatedEndDate', arrIso);
                } else if (item.activityType === 'unloading') {
                    fd.append('estimatedStartDate', depIso);
                    fd.append('estimatedEndDate', arrIso);
                } else if (item.activityType === 'dooring') {
                    fd.append('estimatedDepartureDate', depIso);
                    fd.append('estimatedArrivalDate', arrIso);
                } else {
                    fd.append('estimatedStartDate', depIso);
                    fd.append('estimatedEndDate', arrIso);
                }

                let finalNotes = (item.notes || '').trim();
                const timestamp = moment().format('DD/MM/YYYY HH:mm');
                const noteBody = (notes || '').trim();
                const reschedEntry = `[${timestamp} | Reschedule: ${reason}] Target Selesai: ${moment(arrIso).format('DD/MM/YYYY')}${noteBody ? ` - ${noteBody}` : ''}`;
                finalNotes = finalNotes !== '' ? `${finalNotes}\n---\n${reschedEntry}` : reschedEntry;
                fd.append('notes', finalNotes);

                const idsToUpdate = allTrackingIds && allTrackingIds.length > 0 ? allTrackingIds : [item.rawId];
                await Promise.all(idsToUpdate.map(id => OrderTrackingService.update(item.orderId!, id, fd)));
            }

            banner.add({
                key: 'reschedule_success',
                variant: 'success',
                icon: faCheck,
                text: `Successfully rescheduled ${item.activityLabel}: ${item.shipName || item.orderNumber || ''}`
            });

            onDismiss(true);
        } catch (e) {
            banner.add({
                key: 'reschedule_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Failed to reschedule. Error: ' + ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    };

    return (
        <Panel
            headerText={`Reschedule Activity`}
            isOpen={isOpen}
            type={PanelType.medium}
            onDismiss={() => onDismiss(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!submitting ? (
                        <>
                            <PrimaryButton
                                text="Save New Schedule"
                                iconProps={{ iconName: 'Calendar' }}
                                disabled={isSubmitDisabled}
                                onClick={handleSubmit}
                            />
                            <DefaultButton text="Cancel" onClick={() => onDismiss(false)} />
                        </>
                    ) : (
                        <Spinner size={SpinnerSize.medium} label="Saving new schedule..." labelPosition="right" />
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 18 }} styles={{ root: { marginTop: 15 } }}>
                {/* Current Item Summary Card */}
                <Stack
                    styles={{
                        root: {
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '14px 16px',
                        }
                    }}
                    tokens={{ childrenGap: 8 }}
                >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <span
                            style={{
                                background: item.activityColor + '20',
                                color: item.activityColor,
                                border: `1px solid ${item.activityColor}40`,
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                            }}
                        >
                            <FontAwesomeIcon icon={item.activityIcon} style={{ marginRight: 5 }} />
                            {item.activityLabel}
                        </span>
                        {item.isOverdue && (
                            <span
                                style={{
                                    background: '#fee2e2',
                                    color: '#b91c1c',
                                    border: '1px solid #fecaca',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                }}
                            >
                                Overdue {item.overdueDays ? `${item.overdueDays} days` : ''}
                            </span>
                        )}
                    </Stack>

                    <Text size="large" style={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.shipName ? `${item.shipName} ${item.voy ? `(Voy: ${item.voy})` : ''}` : item.orderNumber || '-'}
                    </Text>

                    {item.locationText && (
                        <Text size="small" style={{ color: '#64748b' }}>
                            📍 {item.locationText}
                        </Text>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 24 }} styles={{ root: { marginTop: 4 } }}>
                        <div>
                            <Label size="xsmall">Jadwal Awal Mulai:</Label>
                            <Text size="small" style={{ fontWeight: 600 }}>
                                {item.scheduledDateFormatted}
                            </Text>
                        </div>
                        {item.estimatedArrival && (
                            <div>
                                <Label size="xsmall">Target Selesai Awal:</Label>
                                <Text size="small" style={{ fontWeight: 600 }}>
                                    {moment(item.estimatedArrival).format('DD/MM/YYYY')}
                                </Text>
                            </div>
                        )}
                    </Stack>
                </Stack>

                {/* Form Inputs: Tanggal Mulai & Tanggal Selesai Baru */}
                <Stack tokens={{ childrenGap: 14 }}>
                    <Stack horizontal tokens={{ childrenGap: 15 }}>
                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <DatePicker
                                label={getStartDateLabel()}
                                value={newDepartureDate}
                                onSelectDate={(date) => {
                                    if (date) {
                                        setNewDepartureDate(date);
                                        if (newArrivalDate && moment(newArrivalDate).isBefore(moment(date), 'day')) {
                                            setNewArrivalDate(date);
                                        }
                                    }
                                }}
                                formatDate={GeneralService.formatDate}
                                isRequired
                            />
                            <span style={{ fontSize: '11px', color: '#64748b', marginTop: 3, display: 'block' }}>
                                Dari jadwal awal order
                            </span>
                        </Stack.Item>

                        <Stack.Item styles={{ root: { width: '50%' } }}>
                            <DatePicker
                                label={getEndDateLabel()}
                                value={newArrivalDate}
                                minDate={newDepartureDate}
                                onSelectDate={(date) => {
                                    if (date) setNewArrivalDate(date);
                                }}
                                formatDate={GeneralService.formatDate}
                                isRequired
                            />
                            <span style={{ fontSize: '11px', color: '#0284c7', marginTop: 3, display: 'block', fontWeight: 600 }}>
                                Pilih target selesai baru (mundur)
                            </span>
                        </Stack.Item>
                    </Stack>

                    <Dropdown
                        label="Reschedule / Delay Reason"
                        options={RESCHEDULE_REASONS}
                        selectedKey={reason}
                        onChange={(_, opt) => {
                            if (opt) setReason(opt.key as string);
                        }}
                        required
                    />

                    {item.notes && item.notes.trim() !== '' && (
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '10px 12px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <FontAwesomeIcon icon={faClockRotateLeft} style={{ color: '#64748b', fontSize: 11 }} />
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    Riwayat Catatan Sebelumnya:
                                </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#334155', whiteSpace: 'pre-line', maxHeight: '100px', overflowY: 'auto', lineHeight: 1.4 }}>
                                {item.notes}
                            </div>
                        </div>
                    )}

                    <TextField
                        label="Catatan Tambahan Reschedule (Opsional)"
                        multiline
                        rows={2}
                        value={notes}
                        onChange={(_, val) => setNotes(val || '')}
                        placeholder="Masukkan alasan / detail pendukung perubahan jadwal..."
                        description="Catatan reschedule ini akan ditambahkan ke alur timeline proses tanpa menghapus catatan sebelumnya."
                    />
                </Stack>
            </Stack>
        </Panel>
    );
};

export default RescheduleModal;
